import Api, { Notification } from "@/api";
import * as Pages from "@/components/Pages";
import * as Notifications from "@/models/notifications";
import * as Signals from "@/signals";
import * as React from "react";
import classNames from "classnames";

import { IconSparkles, IconX, Avatar, SecondaryButton, FormattedTime, TextSeparator } from "turboui";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import ActivityHandler from "@/features/activities";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageModule } from "@/routes/types";
import { useNavigateTo } from "@/routes/useNavigateTo";
import { usePaths } from "../../routes/paths";
import { Link, useSearchParams } from "react-router-dom";

import { CategoryId, CategoryMeta, CATEGORY_META, CATEGORY_ORDER, categoryOf, isCategoryId } from "./categories";

export default { name: "NotificationsPage", loader, Page } as PageModule;

interface LoaderResult {
  notifications: Notification[];
}

async function loader(): Promise<LoaderResult> {
  const data = await Api.notifications.list({
    page: 1,
    perPage: 100,
  });

  return {
    notifications: data.notifications as Notification[],
  };
}

type LaneId = Exclude<CategoryId, "all">;
type Grouped = Record<LaneId, Notification[]>;

function Page() {
  const onLoad = () => Signals.publish(Signals.LocalSignal.RefreshNotificationCount);

  return (
    <Pages.Page title="Notifications" onLoad={onLoad}>
      <NotificationsInbox />
    </Pages.Page>
  );
}

function NotificationsInbox() {
  const { notifications } = Pages.useLoadedData<LoaderResult>();
  const [searchParams] = useSearchParams();

  const param = searchParams.get("category");
  const active: CategoryId = isCategoryId(param) ? param : "all";

  const grouped = React.useMemo(() => groupByCategory(notifications), [notifications]);
  const totalUnread = React.useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  return (
    <div className="px-5 sm:px-8 lg:px-10 py-8 w-full max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-content-dimmed text-sm mt-1">Filter by type, or browse everything below.</p>
        </div>
        {totalUnread > 0 && <MarkAllReadButton />}
      </div>

      {notifications.length === 0 ? (
        <EmptyAll />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[232px_1fr] gap-6 items-start">
          <CategoryRail active={active} grouped={grouped} totalUnread={totalUnread} />
          <MainPane active={active} grouped={grouped} />
        </div>
      )}
    </div>
  );
}

function groupByCategory(notifications: Notification[]): Grouped {
  const grouped: Grouped = { mentions: [], assigned: [], checkins: [], discussions: [], updates: [] };

  for (const n of notifications) {
    grouped[categoryOf(n.activity?.action)].push(n);
  }

  for (const lane of CATEGORY_ORDER) {
    grouped[lane].sort(byUnreadThenRecent);
  }

  return grouped;
}

// Unread first, then most-recent first — so a lane reads as a triage queue while
// still keeping every previously-read notification visible below.
function byUnreadThenRecent(a: Notification, b: Notification): number {
  const readDiff = Number(!!a.read) - Number(!!b.read);
  if (readDiff !== 0) return readDiff;
  return (b.insertedAt || "").localeCompare(a.insertedAt || "");
}

function unreadCount(items: Notification[]): number {
  return items.filter((n) => !n.read).length;
}

function CategoryRail({ active, grouped, totalUnread }: { active: CategoryId; grouped: Grouped; totalUnread: number }) {
  // Only surface a lane that actually has notifications (read or unread).
  const visibleLanes = CATEGORY_ORDER.filter((id) => grouped[id].length > 0);

  return (
    <aside
      className="bg-surface-base border border-stroke-base rounded-2xl p-3 lg:sticky lg:top-4"
      data-test-id="notif-rail"
    >
      <div className="text-xs font-bold uppercase tracking-wide text-content-subtle px-2 pt-1 pb-2">Inbox</div>
      <nav className="flex flex-col gap-0.5">
        <RailItem id="all" active={active === "all"} unread={totalUnread} />
        {visibleLanes.map((id) => (
          <RailItem key={id} id={id} active={active === id} unread={unreadCount(grouped[id])} />
        ))}
      </nav>
    </aside>
  );
}

function RailItem({ id, active, unread }: { id: CategoryId; active: boolean; unread: number }) {
  const meta: CategoryMeta = CATEGORY_META[id];
  const search = id === "all" ? "" : "category=" + id;

  const rowClass = classNames(
    "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors",
    active
      ? "bg-blue-50 dark:bg-blue-950/40 font-semibold text-content-base shadow-[inset_2px_0_0_var(--color-brand-1)]"
      : "text-content-dimmed hover:bg-surface-dimmed hover:text-content-base",
  );

  return (
    <Link to={{ search }} className={rowClass} data-test-id={`notif-cat-${id}`}>
      {id !== "all" && (
        <span
          className={classNames("w-[22px] h-[22px] rounded-md flex items-center justify-center shrink-0", meta.tile)}
        >
          {meta.icon}
        </span>
      )}
      <span className="flex-1 truncate">{meta.label}</span>
      {unread > 0 && (
        <span
          className={classNames(
            "text-[11px] font-bold rounded-full px-1.5 shrink-0",
            active ? "bg-brand-1 text-white-1" : "text-link-base",
          )}
        >
          {unread}
        </span>
      )}
    </Link>
  );
}

function MainPane({ active, grouped }: { active: CategoryId; grouped: Grouped }) {
  if (active === "all") {
    const lanes = CATEGORY_ORDER.filter((id) => grouped[id].length > 0);

    return (
      <div>
        <h2 className="text-lg font-bold mb-4">All notifications</h2>
        {lanes.length === 0 ? <EmptyAll /> : lanes.map((id) => <CategoryGroup key={id} id={id} items={grouped[id]} />)}
      </div>
    );
  }

  const meta: CategoryMeta = CATEGORY_META[active];
  const items = grouped[active];

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <span className={classNames("w-6 h-6 rounded-md flex items-center justify-center shrink-0", meta.tile)}>
          {meta.icon}
        </span>
        <h2 className="text-lg font-bold">{meta.label}</h2>
      </div>

      {items.length === 0 ? <EmptyCategory label={meta.label} /> : <NotificationList items={items} />}
    </div>
  );
}

function CategoryGroup({ id, items }: { id: LaneId; items: Notification[] }) {
  const meta: CategoryMeta = CATEGORY_META[id];

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2.5">
        <span className={classNames("w-5 h-5 rounded-md flex items-center justify-center shrink-0", meta.tile)}>
          {meta.icon}
        </span>
        <span className="text-xs font-bold text-content-accent">{meta.label}</span>
        <div className="flex-1 h-px bg-stroke-base" />
      </div>
      <NotificationList items={items} />
    </div>
  );
}

function NotificationList({ items }: { items: Notification[] }) {
  return (
    <div className="bg-surface-base border border-stroke-base rounded-2xl overflow-hidden divide-y divide-stroke-base">
      {items.map((n) => (
        <ErrorBoundary key={n.id} fallback={<UnrenderableNotification />}>
          <NotificationItem notification={n} />
        </ErrorBoundary>
      ))}
    </div>
  );
}

// A notification can outlive the thing it points at. Isolating each row means one
// dead reference costs a single placeholder line, not the entire inbox.
function UnrenderableNotification() {
  return (
    <div className="px-4 py-3 text-sm text-content-dimmed">
      This notification can't be shown. The item it pointed to was deleted.
    </div>
  );
}

function MarkAllReadButton() {
  const refresh = Pages.useRefresh();
  const [markAllRead, { loading }] = Notifications.useMarkAllNotificationsAsRead();

  const onClick = React.useCallback(async () => {
    await markAllRead({});
    refresh();
  }, [markAllRead]);

  return (
    <SecondaryButton size="xs" testId="mark-all-read" onClick={onClick} loading={loading}>
      Mark all read
    </SecondaryButton>
  );
}

function NotificationItem({ notification }: { notification: Notification }) {
  const paths = usePaths();
  const formattedTimePreferences = useFormattedTimePreferences();
  const activity = notification.activity!;
  const author = activity.author;
  const testId = "notification-item-" + activity.action;

  const goToActivity = useNavigateTo(ActivityHandler.pagePath(paths, activity));
  const [mark] = Notifications.useMarkNotificationAsRead();
  const refresh = Pages.useRefresh();

  const clickHandler = React.useCallback(async () => {
    await mark({ id: notification.id });
    goToActivity();
  }, []);

  const closeHandler = React.useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await mark({ id: notification.id });
    refresh();
  }, []);

  const unread = !notification.read;

  return (
    <div
      className={classNames(
        "relative flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors group",
        unread
          ? "bg-blue-50/60 dark:bg-blue-950/30 hover:bg-blue-50 dark:hover:bg-blue-950/50"
          : "opacity-80 hover:opacity-100 hover:bg-surface-highlight",
      )}
      onClick={clickHandler}
      data-test-id={testId}
    >
      {unread && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-brand-1" />}

      <div className="shrink-0">{author && <Avatar person={author} size="small" />}</div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-content-accent font-semibold">
          <ActivityHandler.NotificationTitle activity={activity} />
        </div>

        <div className="text-content-dimmed font-medium text-sm leading-snug">
          <ActivityHandler.NotificationLocation activity={activity} />
          {author && (
            <>
              <TextSeparator />
              {author.fullName}
            </>
          )}
          <TextSeparator />
          <FormattedTime {...formattedTimePreferences} time={activity.insertedAt} format="long-date" />
        </div>
      </div>

      {unread && (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded opacity-0 group-hover:opacity-100 cursor-pointer p-2 bg-surface-base shadow-sm"
          onClick={closeHandler}
          data-test-id="mark-read"
        >
          <IconX size={16} className="hover:text-content-accent" />
        </div>
      )}
    </div>
  );
}

function EmptyAll() {
  return (
    <div className="px-12 pt-16 py-20 text-content-accent font-medium flex items-center flex-col gap-2">
      <IconSparkles className="text-yellow-500" />
      You're all caught up.
    </div>
  );
}

function EmptyCategory({ label }: { label: string }) {
  return (
    <div className="bg-surface-base border border-stroke-base rounded-2xl px-8 py-14 text-center text-content-dimmed">
      No {label.toLowerCase()} notifications.
    </div>
  );
}
